const normalizeText = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export const normalizeAssistantProvider = (value) => normalizeText(value || 'OpenAI');

export const isRagFlowAssistant = (assistant) =>
  normalizeAssistantProvider(assistant?.proveedorIA || assistant?.ProveedorIA) === 'ragflow';

export const getAssistantProcessingState = (assistant) =>
  assistant?.estadoProcesamiento ||
  assistant?.EstadoProcesamiento ||
  assistant?.estado ||
  assistant?.Estado ||
  '';

export const getAssistantProcessingPercent = (assistant) => {
  const raw =
    assistant?.porcentajeProcesamiento ??
    assistant?.PorcentajeProcesamiento ??
    assistant?.porcentajeAvance ??
    assistant?.PorcentajeAvance;

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? Math.min(100, Math.max(0, Math.round(parsed))) : null;
};

const hasRagFlowDataset = (assistant) =>
  Boolean(assistant?.idRagFlowDataset || assistant?.IdRagFlowDataset);

const hasCompletedDatasetWithIssue = (assistant) => {
  const state = normalizeText(getAssistantProcessingState(assistant));
  const percent = getAssistantProcessingPercent(assistant);
  return ['error', 'fallido', 'failed', 'fail'].includes(state) &&
    percent != null &&
    percent >= 100 &&
    hasRagFlowDataset(assistant);
};

export const isAssistantProcessingReady = (assistant) => {
  if (!isRagFlowAssistant(assistant)) return true;

  const state = normalizeText(getAssistantProcessingState(assistant));
  const percent = getAssistantProcessingPercent(assistant);
  if (hasCompletedDatasetWithIssue(assistant)) {
    return true;
  }

  if (isAssistantProcessingError(assistant)) {
    return false;
  }

  if (['finalizado', 'finalizada', 'listo', 'ready', 'done', 'completed', 'completado'].includes(state)) {
    return true;
  }

  if (!state && (assistant?.idRagFlowDataset || assistant?.IdRagFlowDataset) && (assistant?.idRagFlowChat || assistant?.IdRagFlowChat)) {
    return true;
  }

  return percent != null && percent >= 100;
};

export const isAssistantProcessingError = (assistant) => {
  const state = normalizeText(getAssistantProcessingState(assistant));
  if (hasCompletedDatasetWithIssue(assistant)) {
    return false;
  }

  return ['error', 'fallido', 'failed', 'fail'].includes(state);
};

export const getAssistantProcessingLabel = (assistant) => {
  const state = normalizeText(getAssistantProcessingState(assistant));
  if (hasCompletedDatasetWithIssue(assistant)) {
    return 'Listo';
  }

  if (isAssistantProcessingError(assistant)) {
    return 'Error';
  }
  if (['finalizado', 'finalizada', 'listo', 'ready', 'done', 'completed', 'completado'].includes(state)) {
    return 'Listo';
  }
  if (['pendiente', 'encolado', 'queued'].includes(state)) {
    return 'Pendiente';
  }
  if (['procesando', 'enproceso', 'processing', 'running', 'parsing'].includes(state)) {
    return 'Procesando';
  }
  return isAssistantProcessingReady(assistant) ? 'Listo' : 'Pendiente';
};

export const getAssistantProcessingClassName = (assistant) => {
  const label = getAssistantProcessingLabel(assistant).toLowerCase();
  if (label === 'listo') return 'ready';
  if (label === 'error') return 'error';
  if (label === 'pendiente') return 'pending';
  return 'processing';
};

export const getAssistantProcessingDetail = (assistant) =>
  assistant?.detalleProcesamiento ||
  assistant?.DetalleProcesamiento ||
  '';

export const hasAssistantProcessingWarning = (assistant) => {
  if (hasCompletedDatasetWithIssue(assistant)) {
    return true;
  }

  const explicitValue =
    assistant?.tieneAdvertenciasProcesamiento ??
    assistant?.TieneAdvertenciasProcesamiento ??
    assistant?.documentosConAdvertencia ??
    assistant?.DocumentosConAdvertencia;

  if (explicitValue != null) {
    if (typeof explicitValue === 'number') return explicitValue > 0;
    if (typeof explicitValue === 'boolean') return explicitValue;
    if (Array.isArray(explicitValue)) return explicitValue.length > 0;
  }

  const detail = normalizeText(getAssistantProcessingDetail(assistant));
  return detail.includes('advertencia') ||
    detail.includes('warning') ||
    detail.includes('chunks indexados') ||
    detail.includes('no pudo ser bien procesado');
};
